import React, { useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, Globe, ExternalLink, Unlink, CheckCircle2 } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Checkbox } from "@pgs-core/ui/components/checkbox";
import { Input } from "@pgs-core/ui/components/input";
import { Card, CardHeader, CardTitle, CardContent } from "@pgs-core/ui/components/card";
import { Badge } from "@pgs-core/ui/components/badge";
import { useApiQuery } from "@pgs-core/portal-lib/hooks";
import { normalizeDomain } from "@pgs-core/config";
import { DomainRecordChecklist } from "./domain-record-rows";
import { STATUS_BADGE, STAGE_COPY, friendlyDomainError, checkedAgo } from "../lib/domain-status";
import type { ApiClient } from "@pgs-core/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";
import type { DomainStatusResponse } from "@pgs-core/types";

// State-aware custom-domain connection panel: input stage → DNS setup
// checklist (per-hostname TXT + CNAME records) → connected/degraded states.
// While a hostname is pending it re-syncs against Cloudflare every 60s; the
// Re-check button triggers the same sync on demand.

const POLL_INTERVAL_MS = 60_000;

export function DomainPanel({ client, api, onClientUpdated }: {
  client: AdminClient;
  api: ApiClient | null;
  onClientUpdated: (client: AdminClient) => void;
}) {
  const [input, setInput] = useState("");
  const [includeWww, setIncludeWww] = useState(true);
  const [actionError, setActionError] = useState("");
  const [busy, setBusy] = useState<"connect" | "recheck" | "disconnect" | "addwww" | null>(null);

  const query = useApiQuery<DomainStatusResponse>(
    () => (api ? api.request<DomainStatusResponse>(`/admin/clients/${client.id}/domain`) : null),
    [api, client.id]
  );
  const domain = query.data?.domain ?? null;

  // The client row elsewhere on the page (custom-domain chip, list search)
  // derives from the same mapping — refresh it after any mutation here.
  const refreshClient = async () => {
    if (!api) return;
    try {
      const body = await api.request<{ client?: AdminClient }>(`/admin/clients/${client.id}`);
      if (body?.client) onClientUpdated(body.client);
    } catch {
      // Non-fatal: the mutation already succeeded; only the chip is stale.
    }
  };

  const runAction = async (kind: "connect" | "recheck" | "disconnect" | "addwww", request: () => Promise<DomainStatusResponse>) => {
    if (!api || busy) return;
    setBusy(kind);
    setActionError("");
    try {
      const body = await request();
      query.setData(body);
      if (kind !== "recheck") await refreshClient();
      if (kind === "connect") setInput("");
    } catch (err) {
      setActionError(friendlyDomainError((err as Error).message));
    } finally {
      setBusy(null);
    }
  };

  const connect = () => {
    if (!api) return;
    if (!normalizeDomain(input.trim().toLowerCase())) {
      setActionError("That doesn't look like a valid domain. Enter it like example.com.");
      return;
    }
    void runAction("connect", () =>
      api.request<DomainStatusResponse>(`/admin/clients/${client.id}/domain`, {
        method: "POST",
        body: JSON.stringify({ domain: input.trim().toLowerCase(), include_www: includeWww }),
      })
    );
  };

  const addWww = () => {
    if (!api) return;
    void runAction("addwww", () =>
      api.request<DomainStatusResponse>(`/admin/clients/${client.id}/domain/www`, { method: "POST" })
    );
  };

  const recheck = () => {
    if (!api) return;
    void runAction("recheck", () =>
      api.request<DomainStatusResponse>(`/admin/clients/${client.id}/domain/sync`, { method: "POST" })
    );
  };

  const disconnect = () => {
    if (!api || !domain) return;
    const hasWww = domain.records.some((r) => r.is_www);
    const ok = window.confirm(
      `Disconnect ${domain.root_domain}? The site stops being served on this domain${
        hasWww ? ` (and www.${domain.root_domain})` : ""
      } immediately.`
    );
    if (!ok) return;
    void runAction("disconnect", () =>
      api.request<DomainStatusResponse>(`/admin/clients/${client.id}/domain`, { method: "DELETE" })
    );
  };

  // 60s background sync while any hostname is still pending. Single-flight via
  // the busy state; stops as soon as the domain is active/degraded/gone.
  const pending = domain !== null &&
    (domain.overall_status === "provisioning" || domain.overall_status === "pending_dns");
  const pollingRef = useRef(false);
  useEffect(() => {
    if (!api || !pending) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      try {
        const body = await api.request<DomainStatusResponse>(
          `/admin/clients/${client.id}/domain/sync`,
          { method: "POST" }
        );
        if (!cancelled) {
          query.setData(body);
          if (body.domain?.overall_status === "active") await refreshClient();
        }
      } catch {
        // Transient — the next tick or a manual re-check will recover.
      } finally {
        pollingRef.current = false;
      }
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setData/refresh are stable enough per client
  }, [api, client.id, pending]);

  if (!client.pages_project_name) return null;

  const badge = domain ? STATUS_BADGE[domain.overall_status] : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" /> Custom domain
          </CardTitle>
          {badge && <Badge variant={badge.variant}>{badge.label}</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        {query.loading && !query.data ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading domain status…
          </div>
        ) : domain === null ? (
          <div>
            <p className="text-sm text-muted-foreground">
              Connect the client's own domain.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Input
                value={input}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter") connect(); }}
                placeholder="barrowcafe.co.uk"
                disabled={busy !== null || !api}
                className="h-8 w-64 text-sm"
              />
              <Button size="sm" disabled={busy !== null || !api || !input.trim()} onClick={connect}>
                {busy === "connect" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                {busy === "connect" ? "Connecting…" : "Connect"}
              </Button>
            </div>
            <label className="mt-2.5 flex w-fit cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={includeWww}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeWww(e.target.checked)}
                disabled={busy !== null || !api}
              />
              Also set up the www subdomain (can be added later)
            </label>
            {query.error && <p className="mt-2 text-xs text-destructive">{friendlyDomainError(query.error)}</p>}
          </div>
        ) : (
          <div className="space-y-3">
            {domain.overall_status === "active" ? (
              <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/5 p-3.5 text-sm">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                <span className="font-medium">{STAGE_COPY.active}</span>
                <a
                  href={`https://${domain.root_domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto flex shrink-0 items-center gap-1 font-medium text-primary hover:underline"
                >
                  {domain.root_domain} <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{STAGE_COPY[domain.overall_status]}</p>
            )}

            {domain.overall_status !== "active" && (
              <div className="space-y-2">
                {domain.records.map((record) => (
                  <DomainRecordChecklist key={record.hostname} record={record} />
                ))}
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={busy !== null} onClick={recheck}>
                {busy === "recheck"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <RefreshCw className="h-3.5 w-3.5" />}
                {busy === "recheck" ? "Checking…" : "Re-check DNS"}
              </Button>
              {!domain.records.some((r) => r.is_www) && (
                <Button size="sm" variant="outline" disabled={busy !== null} onClick={addWww}>
                  {busy === "addwww" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {busy === "addwww" ? "Adding…" : `Add www.${domain.root_domain}`}
                </Button>
              )}
              <span className="text-xs text-muted-foreground">{checkedAgo(domain.last_synced_at)}</span>
              <Button
                size="sm"
                variant="ghost"
                disabled={busy !== null}
                onClick={disconnect}
                className="ml-auto text-destructive hover:text-destructive"
              >
                {busy === "disconnect"
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Unlink className="h-3.5 w-3.5" />}
                Disconnect
              </Button>
            </div>
          </div>
        )}

        {actionError && <p className="mt-2 text-xs text-destructive">{actionError}</p>}
      </CardContent>
    </Card>
  );
}
